/**
 * recipe.js — Carousel Composer slide lists: build a carousel from text, and
 * write the current carousel back out as the same text.
 *
 * One line per slide:
 *   DSC09418                         single
 *   DSC09424 + DSC09433              stacked pair
 *   DJI_0471 | DJI_0494              side by side
 *   DSC09504 >> 3                    one wide photo across 3 seamless slides
 *   hero: A, B, C, D                 A across the top, B tall left, C over D right
 *   hero 50%: A, B, C, D             same, top photo takes 50%
 *   grid-quarter: A, B, C, D         any layout from the registry, named by its id
 * Numbering, blank lines and # comments are ignored; partial names match.
 *
 * Every layout the composer can build has a form here, so pasting the box back in
 * rebuilds what you had. A line whose layout id is unknown falls through to the
 * ordinary pair/single rules rather than guessing.
 */
import { cellCount, heroSplit, makePano, newCell } from './geometry.js';
import { getLayout } from './layouts.js';
import { findImage } from './library.js';

// -> { slides, panos, missing }. The caller decides append vs replace, and
// treats an empty slides array as "No photos matched those lines."
export function parseRecipe(text, lib, uid) {
  const made = [];
  const panos = {};
  const missing = [];
  String(text || '').split('\n').forEach((raw) => {
    let line = raw.split('#')[0].trim();
    if (!line) return;
    line = line.replace(/^\s*\d+\s*[).:-]\s*/, '');
    if (!line) return;
    const hero = line.match(/^hero\s*(\d{1,2})?\s*%?\s*:\s*(.+)$/i);
    if (hero) {
      const parts = hero[2].split(/\s*[,+]\s*/).map((x) => x.trim()).filter(Boolean).slice(0, 4);
      const ids = parts.map((x) => { const im = findImage(lib, x); if (!im) missing.push(x); return im ? im.id : null; });
      if (!ids.some(Boolean)) return;
      const cells = [];
      for (let i = 0; i < 4; i++) cells.push(ids[i] ? newCell(ids[i]) : null);
      const sl = { id: uid(), layout: 'hero3', cells };
      if (hero[1]) sl.split = Math.min(70, Math.max(25, +hero[1])) / 100;
      made.push(sl);
      return;
    }
    // a registry layout names itself: "grid-quarter: A, B, C, D"
    const named = line.match(/^([a-z][a-z0-9-]*)\s*:\s*(.+)$/i);
    const reg = named ? getLayout(named[1].toLowerCase()) : null;
    if (reg) {
      const n = cellCount(reg.id);
      const parts = named[2].split(/\s*[,+]\s*/).map((x) => x.trim()).filter(Boolean).slice(0, n);
      const ids = parts.map((x) => { const im = findImage(lib, x); if (!im) missing.push(x); return im ? im.id : null; });
      if (!ids.some(Boolean)) return;
      const cells = [];
      for (let i = 0; i < n; i++) cells.push(ids[i] ? newCell(ids[i]) : null);
      made.push({ id: uid(), layout: reg.id, cells });
      return;
    }
    const pano = line.match(/^(.*?)\s*>>\s*(\d+)\s*$/);
    if (pano) {
      const im = findImage(lib, pano[1]);
      if (!im) { missing.push(pano[1].trim()); return; }
      const p = makePano(im.id, parseInt(pano[2], 10), uid);
      panos[p.gid] = p.group;
      made.push(...p.slides);
      return;
    }
    const isRow = line.indexOf('|') >= 0;
    const parts = line.split(isRow ? '|' : '+').map((s) => s.trim()).filter(Boolean);
    const ids = parts.map((p) => {
      const im = findImage(lib, p);
      if (!im) missing.push(p);
      return im ? im.id : null;
    });
    if (!ids.some(Boolean)) return;
    const layout = parts.length === 1 ? 'single' : (isRow ? 'row' : 'stack');
    const n = cellCount(layout);
    const cells = [];
    for (let i = 0; i < n; i++) cells.push(ids[i] ? newCell(ids[i]) : null);
    made.push({ id: uid(), layout, cells });
  });
  return { slides: made, panos, missing };
}

export function layoutAsText(slides, panos, imgById) {
  const seen = new Set();
  const lines = [];
  slides.forEach((sl) => {
    const g = sl.pano && panos ? panos[sl.pano.gid] : null;
    if (g) {
      if (seen.has(sl.pano.gid)) return;
      seen.add(sl.pano.gid);
      const im = imgById(g.imgId);
      lines.push((lines.length + 1) + '. ' + (im ? im.name : '?') + ' >> ' + g.n);
      return;
    }
    const reg = getLayout(sl.layout);
    if (reg) {
      // by id, not by name: the id is what parseRecipe reads back, and a name with
      // a space in it ("Quarter Grid") would not survive the round trip
      const rn = sl.cells.map((c) => { const im = c && imgById(c.imgId); return im ? im.name : '?'; });
      lines.push((lines.length + 1) + '. ' + sl.layout + ': ' + rn.join(', '));
      return;
    }
    if (sl.layout === 'hero3') {
      const hn = sl.cells.map((c) => { const im = c && imgById(c.imgId); return im ? im.name : '?'; });
      const pct = Math.round(heroSplit(sl) * 100);
      lines.push((lines.length + 1) + '. hero' + (pct !== 42 ? ' ' + pct + '%' : '') + ': ' + hn.join(', '));
      return;
    }
    const names = sl.cells.map((c) => { const im = c && imgById(c.imgId); return im ? im.name : '?'; });
    const j = sl.layout === 'row' ? ' | ' : ' + ';
    lines.push((lines.length + 1) + '. ' + names.join(sl.layout === 'single' ? '' : j));
  });
  return lines.join('\n');
}
